import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Card from '@/components/base/Card';
import EmptyState from '@/components/base/EmptyState';
import ErrorState from '@/components/base/ErrorState';
import ImageLightbox from '@/components/base/ImageLightbox';
import Skeleton from '@/components/base/Skeleton';
import { useToast } from '@/context/ToastContext';
import { downloadFileFromUrl, receiptFileName } from '@/lib/files';
import { formatCurrency, formatDate } from '@/lib/format';
import { systemSettings } from '@/mocks/settings';
import { fetchSettlementById } from '@/services/financeService';
import type { SettlementRequest } from '@/types';
import SettlementStatusBadge from '@/pages/dashboard/finance/components/SettlementStatusBadge';

export default function SettlementDetailPage() {
  const { t, i18n } = useTranslation();
  const { settlementId = '' } = useParams();

  const { pushToast } = useToast();
  const [settlement, setSettlement] = useState<SettlementRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const load = useCallback(async () => {
    if (!settlementId) return;
    setLoading(true);
    setError(false);
    setNotFound(false);
    try {
      const data = await fetchSettlementById(settlementId);
      if (data) setSettlement(data);
      else setNotFound(true);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [settlementId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDownloadReceipt = useCallback(async () => {
    const url = settlement?.transferReceiptUrl;
    if (!url || downloading) return;
    setDownloading(true);
    try {
      await downloadFileFromUrl(url, receiptFileName(settlement.id, url));
      pushToast({
        title: t('finance.setDetail.downloadStarted'),
        description: t('finance.setDetail.downloadStartedDesc'),
        tone: 'success',
        icon: 'ri-download-2-line',
      });
    } catch {
      pushToast({
        title: t('finance.setDetail.downloadError'),
        description: t('common.errorText'),
        tone: 'error',
        icon: 'ri-error-warning-line',
      });
    } finally {
      setDownloading(false);
    }
  }, [settlement, downloading, pushToast, t]);

  const backLink = (
    <Link
      to="/dashboard/finance?tab=settlements"
      className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-600 transition-colors hover:text-foreground-900"
    >
      <i className="ri-arrow-left-line text-lg rtl:rotate-180" aria-hidden="true" />
      {t('finance.setDetail.back')}
    </Link>
  );

  if (loading) {
    return (
      <div className="space-y-5">
        {backLink}
        <Card className="space-y-4">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-56" />
          <Skeleton className="h-24 w-full" />
        </Card>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="space-y-5">
        {backLink}
        <Card>
          <EmptyState
            icon="ri-file-unknow-line"
            title={t('finance.setDetail.notFound')}
            action={
              <Link
                to="/dashboard/finance?tab=settlements"
                className="inline-flex h-11 items-center rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 hover:bg-primary-600"
              >
                {t('finance.setDetail.notFoundBack')}
              </Link>
            }
          />
        </Card>
      </div>
    );
  }

  if (error || !settlement) {
    return (
      <div className="space-y-5">
        {backLink}
        <Card>
          <ErrorState
            title={t('finance.setDetail.error')}
            description={t('common.errorText')}
            retryLabel={t('common.retry')}
            onRetry={load}
          />
        </Card>
      </div>
    );
  }

  const processed = settlement.status !== 'processing';

  return (
    <div className="space-y-5">
      {backLink}

      <Card padded={false}>
        <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-4 md:px-5">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="lb-ltr-field text-lg font-bold text-foreground-950">{settlement.id}</span>
              <SettlementStatusBadge status={settlement.status} />
            </div>
            <p className="mt-1.5 text-sm text-foreground-600">
              {t('finance.setDetail.requestedAt')}: {formatDate(settlement.requestedAt, i18n.language)}
            </p>
          </div>
          <div className="text-end">
            <p className="text-xs text-foreground-500">{t('finance.set.col.amount')}</p>
            <p className="text-xl font-bold text-foreground-950">
              {formatCurrency(settlement.amount, systemSettings.currency)}
            </p>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Card>
            <h2 className="mb-4 text-base font-semibold text-foreground-950">
              {t('finance.setDetail.infoTitle')}
            </h2>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-xs text-foreground-500">{t('finance.set.col.amount')}</dt>
                <dd className="mt-1 text-sm font-semibold text-foreground-950">
                  {formatCurrency(settlement.amount, systemSettings.currency)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">{t('finance.set.col.status')}</dt>
                <dd className="mt-1">
                  <SettlementStatusBadge status={settlement.status} />
                </dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">{t('finance.set.col.requestedAt')}</dt>
                <dd className="mt-1 text-sm font-medium text-foreground-900">
                  {formatDate(settlement.requestedAt, i18n.language)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">{t('finance.set.col.processedAt')}</dt>
                <dd className="mt-1 text-sm font-medium text-foreground-900">
                  {settlement.processedAt
                    ? formatDate(settlement.processedAt, i18n.language)
                    : t('finance.set.notProcessed')}
                </dd>
              </div>
            </dl>
          </Card>

          {settlement.status === 'rejected' && settlement.rejectionReason ? (
            <Card>
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-rose-100 text-rose-700">
                  <i className="ri-close-circle-line text-xl" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <h2 className="text-base font-semibold text-foreground-950">
                    {t('finance.set.col.reason')}
                  </h2>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-700">
                    {settlement.rejectionReason}
                  </p>
                </div>
              </div>
            </Card>
          ) : null}
        </div>

        <div className="space-y-5 lg:col-span-1">
          <Card>
            <div className="mb-4 flex items-center gap-2">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-receipt-line text-lg" aria-hidden="true" />
              </span>
              <h2 className="text-base font-semibold text-foreground-950">
                {t('finance.setDetail.receiptTitle')}
              </h2>
            </div>

            {settlement.transferReceiptUrl ? (
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => setLightboxOpen(true)}
                  aria-label={t('finance.setDetail.receiptViewFull')}
                  className="group relative mx-auto block h-[30rem] w-full max-w-[20rem] cursor-zoom-in overflow-hidden rounded-lg border border-background-200 bg-background-100"
                >
                  <img
                    src={settlement.transferReceiptUrl}
                    alt={t('finance.setDetail.receiptAlt')}
                    title={t('finance.setDetail.receiptAlt')}
                    className="h-full w-full object-contain"
                  />
                  <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-foreground-950/0 transition-colors duration-150 ease-smooth group-hover:bg-foreground-950/40">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-background-50/90 text-foreground-900 opacity-0 transition-opacity duration-150 ease-smooth group-hover:opacity-100">
                      <i className="ri-zoom-in-line text-xl" aria-hidden="true" />
                    </span>
                  </span>
                </button>
                <p className="flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
                  <i className="ri-zoom-in-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
                  {t('finance.setDetail.receiptZoomHint')}
                </p>
                <button
                  type="button"
                  onClick={handleDownloadReceipt}
                  disabled={downloading}
                  className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {downloading ? (
                    <i className="ri-loader-4-line animate-spin text-lg" aria-hidden="true" />
                  ) : (
                    <i className="ri-download-2-line text-lg" aria-hidden="true" />
                  )}
                  {downloading ? t('finance.setDetail.downloading') : t('finance.setDetail.download')}
                </button>
                <p className="flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
                  <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
                  {t('finance.setDetail.receiptNote')}
                </p>
              </div>
            ) : (
              <p className="flex items-start gap-2 rounded-lg border border-background-200 bg-background-100 px-3.5 py-3 text-xs leading-relaxed text-foreground-600">
                <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
                {processed ? t('finance.setDetail.receiptNone') : t('finance.setDetail.receiptPending')}
              </p>
            )}
          </Card>
        </div>
      </div>

      <p className="flex items-start gap-2 px-1 text-xs leading-relaxed text-foreground-500">
        <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
        {t('finance.demoNote')}
      </p>

      {settlement.transferReceiptUrl ? (
        <ImageLightbox
          open={lightboxOpen}
          src={settlement.transferReceiptUrl}
          alt={t('finance.setDetail.receiptAlt')}
          title={t('finance.setDetail.receiptTitle')}
          onClose={() => setLightboxOpen(false)}
          actions={
            <button
              type="button"
              onClick={handleDownloadReceipt}
              disabled={downloading}
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-background-50/10 px-3 text-xs font-medium text-background-50 transition-colors hover:bg-background-50/20 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
            >
              {downloading ? (
                <i className="ri-loader-4-line animate-spin text-base" aria-hidden="true" />
              ) : (
                <i className="ri-download-2-line text-base" aria-hidden="true" />
              )}
              {downloading ? t('finance.setDetail.downloading') : t('finance.setDetail.download')}
            </button>
          }
        />
      ) : null}
    </div>
  );
}